import Container from "../element/container";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { pdf } from "@react-pdf/renderer";
import Icon from "../element/icon";
import ServiceAgreementPdf from "../pdf/service.agreement.pdf";
import BuyerSalePurchaseAgreementPdf from "../pdf/buyer.sales.purchase.agreement.pdf";
import agreementData from "../pdf/agreement.data";
import AgreementPreview from "../pdf/agreement.preview";
import FullModal from "./full.modal";
import usePinchZoom from "../hook/use.pinch.zoom";

const ZOOMS = [50, 75, 100, 125, 150, 200];

// Reopening the same agreement should not repeat React-PDF's layout work.
const previews = new Map();
function prepareAgreement(type, data) {
  const key = JSON.stringify([type, data]);
  if (!previews.has(key)) {
    const pending = new Promise((resolve) => setTimeout(resolve, 0))
      .then(() =>
        pdf(
          type === "service" ? (
            <ServiceAgreementPdf data={data} />
          ) : (
            <BuyerSalePurchaseAgreementPdf data={data} />
          ),
        ).toBlob(),
      )
      .catch((error) => {
        previews.delete(key);
        throw error;
      });
    previews.set(key, pending);
    if (previews.size > 2) previews.delete(previews.keys().next().value);
  }
  return previews.get(key);
}

export default function AgreementReaderModal({
  show,
  type,
  record,
  accepted,
  onAcceptedChange,
  onClose,
}) {
  const { t } = useTranslation();
  const { profileInfo } = useSelector((state) => state.profile);
  const inputRef = useRef({ record, profileInfo });
  inputRef.current = { record, profileInfo };
  const previewUrlRef = useRef("");
  const bodyRef = useRef(null);
  const frameRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(100);

  const [width, setWidth] = useState(600);
  const [error, setError] = useState("");
  const [reachedEnd, setReachedEnd] = useState(false);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setPreviewUrl("");
    setPageCount(0);
    setPage(1);
    setZoom(100);
    setReachedEnd(false);
    setError("");
    (async () => {
      const data = agreementData(
        inputRef.current.record,
        inputRef.current.profileInfo,
      );
      const blob = await prepareAgreement(type, data);
      if (cancelled) return;
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    })().catch(() => {
      if (!cancelled)
        setError("Unable to load the agreement. Please close and try again.");
    });
    return () => {
      cancelled = true;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = "";
      }
    };
  }, [show, type]);

  useEffect(() => {
    if (!previewUrl || !frameRef.current) return;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, [previewUrl]);

  // Bound to the scrolling body: on a phone the pinch usually starts beside
  // the page, not on it.
  usePinchZoom(bodyRef, {
    min: ZOOMS[0],
    max: ZOOMS[ZOOMS.length - 1],
    enabled: Boolean(previewUrl),
    value: zoom,
    onZoom: setZoom,
  });

  const pageWidth = Math.round((width * zoom) / 100);

  const handleScroll = (event) => {
    const host = event.currentTarget;
    const pages = frameRef.current?.querySelectorAll("[data-page-number]");
    if (!pages?.length) return;
    const bounds = host.getBoundingClientRect();
    const lastPage = pages[pages.length - 1];
    if (
      previewUrl &&
      pageCount &&
      lastPage.getBoundingClientRect().bottom <= bounds.bottom + 8
    )
      setReachedEnd(true);
    const current = Array.from(pages).find(
      (item) =>
        item.getBoundingClientRect().bottom >
        bounds.top + host.clientHeight * 0.35,
    );
    setPage(current ? Number(current.dataset.pageNumber) : pageCount);
  };

  const goToPage = (number) => {
    frameRef.current
      ?.querySelector(`[data-page-number="${number}"]`)
      ?.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  return (
    <FullModal
      independent
      show={show}
      close={onClose}
      closeOnBackdrop={false}
      showCloseButton={false}
      centered
      screenSize="xl"
      className="product-action-modal reservation-modal agreement-reader-modal"
      dialogClassName="action-modal-dialog agreement-reader-dialog"
      contentClassName="agreement-reader-content"
      content={
        <div
          className="reservation-body agreement-reader-body"
          ref={bodyRef}
          onScroll={handleScroll}
        >
          <h2>
            {type === "service"
              ? t("Service Agreement")
              : t("Buyer Sale and Purchase Agreement")}
          </h2>
          <p className="agreement-reader-lead">
            {t("Please read the complete document before continuing.")}
          </p>
          <Container tag="div" className="agreement-reader-toolbar">
            <Container tag="div" className="agreement-reader-controls">
              <Container tag="div" className="agreement-reader-group">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => goToPage(page - 1)}
                  aria-label="Previous page"
                >
                  <Icon icon="mingcute:left-line" />
                </button>
                <span className="agreement-reader-page">
                  {t("Page")} {page} / {pageCount || "-"}
                </span>
                <button
                  type="button"
                  disabled={!pageCount || page >= pageCount}
                  onClick={() => goToPage(page + 1)}
                  aria-label="Next page"
                >
                  <Icon icon="mingcute:right-line" />
                </button>
              </Container>
              <Container tag="div" className="agreement-reader-group">
                <button
                  type="button"
                  disabled={!pageCount || zoom <= ZOOMS[0]}
                  onClick={() =>
                    setZoom(
                      [...ZOOMS].reverse().find((step) => step < zoom) ||
                        ZOOMS[0],
                    )
                  }
                  aria-label="Zoom out"
                >
                  <Icon icon="mingcute:minimize-line" />
                </button>
                <span>{zoom}%</span>
                <button
                  type="button"
                  disabled={!pageCount || zoom >= ZOOMS[ZOOMS.length - 1]}
                  onClick={() =>
                    setZoom(
                      ZOOMS.find((step) => step > zoom) ||
                        ZOOMS[ZOOMS.length - 1],
                    )
                  }
                  aria-label="Zoom in"
                >
                  <Icon icon="mingcute:add-line" />
                </button>
              </Container>
            </Container>
          </Container>
          <div
            className="agreement-reader-frame"
            ref={frameRef}
            aria-label="Agreement document"
          >
            {error ? (
              <p role="alert">{error}</p>
            ) : !previewUrl ? (
              <p>{t("Preparing agreement...")}</p>
            ) : (
              <AgreementPreview
                key={previewUrl}
                url={previewUrl}
                width={pageWidth}
                scrollRoot={bodyRef}
                onReady={setPageCount}
                onError={() =>
                  setError(
                    "Unable to display the agreement. Please close and try again.",
                  )
                }
              />
            )}
          </div>
          <p className="agreement-reader-hint">
            {reachedEnd
              ? t("You have reached the end of the document.")
              : t("Scroll to the bottom of the document to continue.")}
          </p>
          <button
            type="button"
            className="action-modal-primary agreement-reader-agree"
            disabled={!reachedEnd || !!error}
            onClick={() => {
              onAcceptedChange(true);
              onClose();
            }}
          >
            {t("I Have Read and Agree")}
          </button>
        </div>
      }
    />
  );
}
